import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import { LeftArrow } from "../../components/icons";
import { SolidColorButton } from "../../components/buttons";
import CurrentAveragedRating from "../../components/starRating/dashboardStarRating";
import { apiFetchRatedSkitsForCurrentUserDashboard } from "../../api/backend/skits";
import { categories } from "../../constants/categories";
import TooltipDots from "../../components/toolTipDots/toolTipDots";
import {
  CatagoriesIcon,
  PencilIcon,
  LightBulbIcon,
  SkitsOlympicLogoIcon,
} from "../../components/icons/index";
import Loader from "../../components/loader/loader";
import "./currentUserSkitDashboardMediumSmallDevice.scss";

const getCategoryById = (id) => {
  return categories.find((category) => category.id === id);
};

const CurrentUserSkitDashboardMediumSmallDevice = () => {
  const { currentMemberId, handleLogout } = useAuth();
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const [ratedSkits, setRatedSkits] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  let navigate = useNavigate();
  const limit = 10; // Number of skits per page

  const fetchSkits = async (currentOffset = 0) => {
    if (!currentMemberId) return;
    setIsLoading(true);
    try {
      const loggedInAccountHolderId = currentMemberId;
      const fetchedSkits = await apiFetchRatedSkitsForCurrentUserDashboard(
        loggedInAccountHolderId,
        limit,
        currentOffset
      );
      // suspension eject
      if (isSuspendedFrom(fetchedSkits) || isSuspendedFrom(fetchedSkits?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread (body first)
      const bodyCount = Number(
        fetchedSkits?.data?.unreadNotificationCount ??
        fetchedSkits?.unreadNotificationCount
      );
      if (Number.isFinite(bodyCount)) {
        notificationsCountFetchedFromMiddleware(bodyCount);
      }
      // 🔔 header fallback
      const headerCount = Number(fetchedSkits?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount)) {
        notificationsCountFetchedFromMiddleware(headerCount);
      }
      // creating the constant so that state always holds the array of skits rather than the entire Axios response object.
      const skitsArray = Array.isArray(fetchedSkits?.data)
        ? fetchedSkits.data
        : Array.isArray(fetchedSkits?.data?.skits)
        ? fetchedSkits.data.skits
        : Array.isArray(fetchedSkits)
        ? fetchedSkits
        : [];
  
      if (currentOffset === 0) {
        setRatedSkits(skitsArray);
      } else {
        setRatedSkits((prevSkits) => [...prevSkits, ...skitsArray]);
      }
      if (skitsArray.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      // 🔔 hydrate from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      // suspension eject (error path)
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
  
      console.error("Error fetching rated skits:", error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch initial skits on component mount or when currentMemberId changes.
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchSkits(0);
  }, [currentMemberId]);

  const handleViewMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchSkits(newOffset);
  };

  return (
    <div
      id="top"
      className="skitDashboardMediumSmallDevicecurrent-user-skit-dashboard med-small-device-view"
    >
      <SolidColorButton
        solidColorBtnImage={
          <LeftArrow
            height="15px"
            width="15px"
            fillColor="#964b00"
            strokeWidth="4"
            strokeColor="#964b00"
          />
        }
        className="gold-btn"
        solidColorBtnText="Back"
        solidColorBtnFontWeight="800"
        solidColorBtnFontFamily="helvetica"
        solidColorBtnTextFontSize=".75rem"
        solidColorBtnFontColor="#964b00"
        solidColorBtnWidth="100px"
        solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
        onClick={() => navigate(-1)}
      />

      {/* Loader */}
      {isLoading && ratedSkits.length === 0 ? (
        <Loader />
      ) : (
        <div
          id="small-med-dashboard-list"
          className="skitDashboardMediumSmallDeviceskits-list"
        >
          {ratedSkits.length > 0
            ? ratedSkits.map((skit) => {
                const category = getCategoryById(skit.categoryid);
                return (
                  <div
                    key={skit.id}
                    className="skitDashboardMediumSmallDeviceskit-item"
                  >
                    <div className="skitDashboardMediumSmallDeviceskit-title">
                      <TooltipDots text={skit.title} maxLength={39} />
                    </div>
                    <div className="skitDashboardMediumSmallDeviceskit-details">
                      <div className="skitDashboardMediumSmallDevicedetail-item">
                        <div className="skitdash-medsmall-icon-container">
                          <PencilIcon
                            fillColor="var(--color-offWhite)"
                            height="25"
                            width="25"
                          />
                        </div>
                        <p className="skitDashboardMediumSmallDevicedetail-title">
                          Story Line
                        </p>
                        <CurrentAveragedRating
                          rating={skit.storyLineAverageScore}
                        />
                      </div>
                      <div className="skitDashboardMediumSmallDevicedetail-item">
                        <div className="skitdash-medsmall-icon-container">
                          <SkitsOlympicLogoIcon
                            fillColor="var(--color-offWhite)"
                            height="25"
                            width="25"
                            strokeColor="var(--color-offWhite)"
                            strokeWidth="2"
                          />
                        </div>
                        <p className="skitDashboardMediumSmallDevicedetail-title">
                          Actor's/(s') Performance
                        </p>
                        <CurrentAveragedRating
                          rating={skit.performanceAverageScore}
                        />
                      </div>
                      <div className="skitDashboardMediumSmallDevicedetail-item">
                        <div className="skitdash-medsmall-icon-container">
                          <LightBulbIcon
                            fillColor="var(--color-offWhite)"
                            height="25"
                            width="25"
                            strokeColor="var(--color-offWhite)"
                            strokeWidth="2"
                          />
                        </div>
                        <p className="skitDashboardMediumSmallDevicedetail-title">
                          Originality
                        </p>
                        <CurrentAveragedRating
                          rating={skit.originalityAverageScore}
                        />
                      </div>
                      <div className="skitDashboardMediumSmallDevicedetail-item">
                        <div className="skitdash-medsmall-icon-container">
                          <CatagoriesIcon fillColor="var(--color-offWhite)" />
                        </div>
                        <p className="skitDashboardMediumSmallDevicedetail-title">
                          {category.title}
                        </p>
                        <CurrentAveragedRating
                          rating={skit.categoryAverageScore}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            : // Only show "No Skit Metrics Found" when not loading.
              !isLoading && (
                <div className="skitDashboardMediumSmallDeviceno-skits-found">
                  <h2>No Skit Metrics Found..</h2>
                </div>
              )}
        </div>
      )}

      {/* View More button, only shown if we have skits and more to load */}
      {!isLoading && ratedSkits.length > 0 && hasMore && (
        <button onClick={handleViewMore}>View More</button>
      )}
    </div>
  );
};

export default CurrentUserSkitDashboardMediumSmallDevice;
